import React from 'react';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Share,
  X,
  Copy,
  Check,
  CaretDown,
  User,
  Envelope,
  Users,
  LinkSimple,
  Plus,
  Lock,
  PencilSimple,
  Eye,
  FilePlus,
  FolderOpen,
  ArrowLeft,
  ClockCounterClockwise,
  Trash
} from 'phosphor-react';

type Permission = 'owner' | 'can-edit' | 'can-view';

interface UserPermission {
  id: string;
  name: string;
  email: string;
  permission: Permission;
  isOwner?: boolean;
}

const PERMISSION_LABELS: Record<Permission, string> = {
  'owner': 'Owner',
  'can-edit': 'Can edit',
  'can-view': 'Can view',
};

const PERMISSION_ICONS: Record<Permission, React.ReactNode> = {
  'owner': <Lock className="w-3.5 h-3.5" weight="fill" />,
  'can-edit': <PencilSimple className="w-3.5 h-3.5" weight="fill" />,
  'can-view': <Eye className="w-3.5 h-3.5" weight="fill" />,
};

interface DocsHeaderProps {
  docId?: string;
  docTitle?: string;
  displayName: string;
  initials: string;
  onCopyLink?: () => void;
  isSideChatOpen?: boolean;
  onToggleSideChat?: () => void;
  // CRUD callbacks
  onCreateNew?: () => void;
  onDuplicate?: () => void;
  onRename?: (newTitle: string) => void;
  onDelete?: () => void;
  onBack?: () => void;
}

export function DocsHeader({
  docTitle,
  initials,
  onCopyLink,
  isSideChatOpen = true,
  onToggleSideChat,
  onCreateNew,
  onDuplicate,
  onRename,
  onDelete,
  onBack
}: DocsHeaderProps) {
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedPermission, setSelectedPermission] = useState<Permission>('can-edit');
  const [isPermissionDropdownOpen, setIsPermissionDropdownOpen] = useState(false);
  const [isLinkPermissionDropdownOpen, setIsLinkPermissionDropdownOpen] = useState(false);
  const [linkPermission, setLinkPermission] = useState<Permission>('can-view');
  const [copied, setCopied] = useState(false);
  const [users, setUsers] = useState<UserPermission[]>([
    {
      id: '1',
      name: 'Você',
      email: 'user@example.com',
      permission: 'owner',
      isOwner: true,
    },
  ]);

  // Mock data for users currently viewing the page
  const [viewingUsers] = useState([
    { id: 'v1', name: 'Jacob Wilson', initials: 'JW', isCurrentUser: false },
    { id: 'v2', name: 'Alexander Wright', initials: 'AW', isCurrentUser: false },
    { id: 'v3', name: 'Charlotte Taylor', initials: 'CT', isCurrentUser: false },
    { id: 'v4', name: 'Você', initials: initials, isCurrentUser: true },
  ]);
  const [isDocDropdownOpen, setIsDocDropdownOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(docTitle || 'Documento');
  const shareDropdownRef = useRef<HTMLDivElement>(null);
  const permissionDropdownRef = useRef<HTMLDivElement>(null);
  const linkPermissionDropdownRef = useRef<HTMLDivElement>(null);
  const docDropdownRef = useRef<HTMLDivElement>(null);
  const [isAvataresHovered, setIsAvataresHovered] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
        setIsShareDropdownOpen(false);
      }
      if (permissionDropdownRef.current && !permissionDropdownRef.current.contains(event.target as Node)) {
        setIsPermissionDropdownOpen(false);
      }
      if (linkPermissionDropdownRef.current && !linkPermissionDropdownRef.current.contains(event.target as Node)) {
        setIsLinkPermissionDropdownOpen(false);
      }
      if (docDropdownRef.current && !docDropdownRef.current.contains(event.target as Node)) {
        setIsDocDropdownOpen(false);
      }
    };

    if (isShareDropdownOpen || isDocDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isShareDropdownOpen]);

  const handleSendInvite = () => {
    if (!email.trim()) return;

    setUsers([
      ...users,
      {
        id: Date.now().toString(),
        name: email.split('@')[0],
        email: email.trim(),
        permission: selectedPermission,
      },
    ]);
    setEmail('');
  };

  const handleCopyLink = async () => {
    if (onCopyLink) {
      onCopyLink();
    } else {
      try {
        if (typeof window === 'undefined') return;
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // ignore
      }
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-[420px] max-md:right-0 z-30 px-6 py-4 max-md:px-4 max-md:py-3 bg-[color:var(--background)]/80 backdrop-blur-sm border-b theme-border shadow-none ${isSideChatOpen ? 'right-[420px]' : 'right-0 w-full'}`}>
      <div className="mx-auto flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row items-center gap-4">
          <Link to="/dashboard" className="mr-auto w-fit">
            <img alt="Logo" width={96} height={48} src="/logo-mini.svg" className="h-8 w-auto" />
          </Link>

          {/* Center space chip */}
          <div className="relative flex-1 flex justify-center max-md:hidden" ref={docDropdownRef}>
            <button
              type="button"
              onClick={() => setIsDocDropdownOpen(!isDocDropdownOpen)}
              className={`flex items-center gap-2 h-10 text-xs font-semibold transition-all`}
              aria-label="Opções do documento"
              title="Opções do documento"
            >
              <svg
                className="inline-flex size-4 fill-[color:var(--foreground-muted)] flex-shrink-0"
                width="20"
                height="20"
                viewBox="0 0 24 24"
              >
                <path d="M12 2a5 5 0 0 1 5 5v2.126c1.725.444 3 2.01 3 3.874v5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-5c0-1.864 1.275-3.43 3-3.874V7a5 5 0 0 1 5-5zm0 11a1 1 0 0 0-1 1v3a1 1 0 1 0 2 0v-3a1 1 0 0 0-1-1zm0-9a3 3 0 0 0-3 3v2h6V7a3 3 0 0 0-3-3z"></path>
              </svg>
              {isRenaming ? (
                <input
                  autoFocus
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIsRenaming(false);
                      if (tempTitle !== docTitle) {
                        onRename?.(tempTitle);
                      }
                    } else if (e.key === 'Escape') {
                      setTempTitle(docTitle || 'Documento');
                      setIsRenaming(false);
                    }
                  }}
                  onBlur={() => {
                    setIsRenaming(false);
                    if (tempTitle !== docTitle) {
                      onRename?.(tempTitle);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-transparent border-none focus:outline-none theme-text text-xs font-semibold w-fit"
                />
              ) : (
                <div
                  className="max-w-[40ch] truncate cursor-text"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setIsRenaming(true);
                  }}
                >
                  {tempTitle || 'Documento'}
                </div>
              )}
              <CaretDown className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${isDocDropdownOpen ? 'rotate-180' : ''}`} weight="bold" />
            </button>

            <AnimatePresence>
              {isDocDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -4, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -4, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-4 ml-2 w-72 rounded-xl border theme-border bg-[color:var(--card-bg)] shadow-2xl z-50 overflow-hidden py-1.5"
                >
                  {[
                    { icon: <FilePlus className="w-4 h-4" />, label: 'Criar um novo documento', shortcut: '⌘N', onClick: () => onCreateNew?.() },
                    { icon: <FolderOpen className="w-4 h-4" />, label: 'Abrir', shortcut: '⌘O', onClick: () => onBack?.() },
                    { icon: <Copy className="w-4 h-4" />, label: 'Fazer uma cópia', onClick: () => onDuplicate?.() },
                    { icon: <PencilSimple className="w-4 h-4" />, label: 'Renomear', shortcut: '↩', onClick: () => setIsRenaming(true) },
                    { type: 'separator' },
                    { icon: <ClockCounterClockwise className="w-4 h-4" />, label: 'Histórico de versões', shortcut: '⌘H', onClick: () => { } },
                    { icon: <Trash className="w-4 h-4 text-red-500" />, label: 'Mover para a lixeira', shortcut: '⌫', onClick: () => onDelete?.(), className: 'text-red-500 hover:bg-red-50/50 dark:hover:bg-red-500/10' },
                    { type: 'separator' },
                    { icon: <ArrowLeft className="w-4 h-4" />, label: 'Voltar', onClick: () => onBack?.() },
                  ].map((item, index) => (
                    item.type === 'separator' ? (
                      <div key={`sep-${index}`} className="h-px bg-current opacity-10 my-1.5 mx-3" />
                    ) : (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => {
                          item.onClick?.();
                          setIsDocDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-sm theme-text text-left flex items-center justify-between transition-all hover:bg-neutral-500/5 group ${item.className || ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="opacity-60 group-hover:opacity-100 transition-opacity">{item.icon}</span>
                          <span className="font-medium">{item.label}</span>
                        </div>
                        {item.shortcut && (
                          <span className="text-[10px] font-medium opacity-40 group-hover:opacity-60 tabular-nums uppercase transition-opacity">
                            {item.shortcut}
                          </span>
                        )}
                      </button>
                    )
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex flex-row items-center gap-2">
          {/* Viewing Users Avatares */}
          <div
            className="relative flex items-center gap-2 max-md:hidden"
            onMouseEnter={() => setIsAvataresHovered(true)}
            onMouseLeave={() => setIsAvataresHovered(false)}
          >
            {viewingUsers.slice(0, 3).map((user) => (
              <div
                key={user.id}
                className="relative"
                title={user.name}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${user.isCurrentUser
                    ? 'bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]'
                    : 'bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] theme-text border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)]'
                    }`}
                >
                  {user.initials}
                </div>
                {!user.isCurrentUser && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-[color:var(--background)]" />
                )}
              </div>
            ))}
            {viewingUsers.length > 3 && (
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] theme-text border-2 border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)]">
                +{viewingUsers.length - 3}
              </div>
            )}

            {/* Tooltip/Popover */}
            <AnimatePresence>
              {isAvataresHovered && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-64 rounded-xl border theme-border theme-bg shadow-2xl z-50 overflow-hidden"
                  onMouseEnter={() => setIsAvataresHovered(true)}
                  onMouseLeave={() => setIsAvataresHovered(false)}
                >
                  <div className="px-4 py-3 border-b theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_95%,transparent)]">
                    <div className="text-xs font-semibold theme-text-muted uppercase tracking-wider">
                      Visualizando agora
                    </div>
                    <div className="text-xs theme-text-muted mt-0.5">
                      {viewingUsers.length} {viewingUsers.length === 1 ? 'pessoa' : 'pessoas'}
                    </div>
                  </div>
                  <div className="py-2 max-h-[300px] overflow-y-auto">
                    {viewingUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-colors"
                      >
                        <div className="relative flex-shrink-0">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${user.isCurrentUser
                              ? 'bg-[color:var(--nt-primary)] text-white border-[color:var(--nt-primary)]'
                              : 'bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] theme-text border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)]'
                              }`}
                          >
                            {user.initials}
                          </div>
                          {!user.isCurrentUser && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-[color:var(--card-bg)]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium theme-text truncate">
                            {user.name}
                            {user.isCurrentUser && (
                              <span className="ml-2 text-xs theme-text-muted">(você)</span>
                            )}
                          </div>
                          <div className="text-xs theme-text-muted">
                            {user.isCurrentUser ? 'Você está editando' : 'Visualizando'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Share button with dropdown */}
          <div className="relative" ref={shareDropdownRef}>
            <button
              type="button"
              onClick={() => setIsShareDropdownOpen(!isShareDropdownOpen)}
              className="inline-flex items-center justify-center h-11 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer bg-[color:var(--nt-primary)] text-white shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom),inset_0_0.125rem_0_0_var(--nt-primary-inset-top)] hover:brightness-95 hover:shadow-[inset_0_-0.125rem_0_0_var(--nt-primary-inset-bottom-hover),inset_0_0.125rem_0_0_var(--nt-primary-inset-top-hover)] max-md:w-11 max-md:px-0"
              aria-label="Compartilhar"
              title="Compartilhar"
            >
              <Share className="inline-flex size-5 max-md:mr-0 mr-2" weight="bold" />
              <span className="max-md:hidden">Compartilhar</span>
            </button>

            <AnimatePresence>
              {isShareDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 w-[440px] rounded-2xl border theme-border theme-bg shadow-2xl overflow-hidden z-50 backdrop-blur-sm bg-[color:var(--card-bg)]"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b theme-border bg-gradient-to-br from-[color:var(--card-bg)] via-[color:color-mix(in_oklab,var(--card-bg)_98%,transparent)] to-[color:color-mix(in_oklab,var(--card-bg)_95%,transparent)]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[color:var(--nt-primary)]/20 to-[color:var(--nt-primary)]/10 flex items-center justify-center border border-[color:var(--nt-primary)]/20">
                        <Share className="w-5 h-5 text-[color:var(--nt-primary)]" weight="bold" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold theme-text">Compartilhar documento</h2>
                        <p className="text-xs theme-text-muted mt-0.5">Gerencie permissões e acesso</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsShareDropdownOpen(false)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[color:color-mix(in_oklab,var(--card-bg)_80%,transparent)] active:scale-95 transition-all theme-text-muted hover:theme-text"
                      aria-label="Fechar"
                    >
                      <X className="w-4 h-4" weight="bold" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-5 space-y-6 max-h-[520px] overflow-y-auto">
                    {/* Invite Section */}
                    <div className="bg-gradient-to-br from-[color:color-mix(in_oklab,var(--card-bg)_98%,transparent)] via-[color:color-mix(in_oklab,var(--card-bg)_96%,transparent)] to-[color:color-mix(in_oklab,var(--card-bg)_94%,transparent)] rounded-xl p-5 border theme-border shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[color:var(--nt-primary)]/20 to-[color:var(--nt-primary)]/10 flex items-center justify-center border border-[color:var(--nt-primary)]/20">
                          <Plus className="w-4.5 h-4.5 text-[color:var(--nt-primary)]" weight="bold" />
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-semibold theme-text">
                            Convidar pessoas
                          </label>
                          <p className="text-xs theme-text-muted mt-0.5">Adicione pessoas por email para colaborar</p>
                        </div>
                      </div>
                      <div className="flex items-stretch gap-2">
                        <div className="flex-1 relative">
                          <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 theme-text-muted pointer-events-none" weight="regular" />
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && email.trim()) {
                                handleSendInvite();
                              }
                            }}
                            placeholder="email@exemplo.com"
                            className="w-full pl-10 pr-3.5 py-2.5 text-sm border theme-border rounded-lg bg-[color:var(--background)] theme-text placeholder:theme-text-muted focus:outline-none focus:ring-2 focus:ring-[color:var(--nt-primary)]/30 focus:border-[color:var(--nt-primary)] transition-all"
                          />
                        </div>
                        <div className="relative" ref={permissionDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsPermissionDropdownOpen(!isPermissionDropdownOpen)}
                            className="px-3.5 py-2.5 text-sm border theme-border rounded-lg bg-[color:var(--background)] theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] active:scale-95 transition-all flex items-center gap-2 min-w-[150px]"
                          >
                            <span className="text-xs flex items-center gap-1.5">
                              {PERMISSION_ICONS[selectedPermission]}
                              {PERMISSION_LABELS[selectedPermission]}
                            </span>
                            <CaretDown className="w-3.5 h-3.5" weight="bold" />
                          </button>
                          <AnimatePresence>
                            {isPermissionDropdownOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.15 }}
                                className="absolute right-0 top-full mt-1.5 w-full rounded-lg border theme-border theme-bg shadow-xl overflow-hidden z-10 backdrop-blur-sm"
                              >
                                {(['can-edit', 'can-view'] as Permission[]).map((perm) => (
                                  <button
                                    key={perm}
                                    type="button"
                                    onClick={() => {
                                      setSelectedPermission(perm);
                                      setIsPermissionDropdownOpen(false);
                                    }}
                                    className={`w-full px-3.5 py-2.5 text-sm text-left transition-colors flex items-center gap-2.5 ${selectedPermission === perm
                                      ? 'bg-[color:var(--nt-primary)]/10 theme-text font-medium border-l-2 border-[color:var(--nt-primary)]'
                                      : 'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] theme-text'
                                      }`}
                                  >
                                    <span className="opacity-70">{PERMISSION_ICONS[perm]}</span>
                                    <span>{PERMISSION_LABELS[perm]}</span>
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        <button
                          type="button"
                          onClick={handleSendInvite}
                          disabled={!email.trim()}
                          className="px-5 py-2.5 text-sm font-semibold text-white bg-[color:var(--nt-primary)] rounded-lg hover:brightness-95 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0 flex items-center gap-2"
                        >
                          <Envelope className="w-4 h-4" weight="bold" />
                          <span>Enviar</span>
                        </button>
                      </div>
                    </div>

                    {/* Users List */}
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[color:var(--nt-primary)]/20 to-[color:var(--nt-primary)]/10 flex items-center justify-center border border-[color:var(--nt-primary)]/20">
                          <Users className="w-4.5 h-4.5 text-[color:var(--nt-primary)]" weight="bold" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold theme-text">
                            Pessoas com acesso
                          </div>
                          <p className="text-xs theme-text-muted mt-0.5">{users.length} {users.length === 1 ? 'pessoa tem acesso' : 'pessoas têm acesso'}</p>
                        </div>
                      </div>
                      {users.length === 0 ? (
                        <div className="text-center py-8 px-4 rounded-xl border-2 border-dashed theme-border bg-[color:color-mix(in_oklab,var(--card-bg)_95%,transparent)]">
                          <User className="w-8 h-8 theme-text-muted mx-auto mb-2 opacity-50" weight="duotone" />
                          <p className="text-sm theme-text-muted">Nenhuma pessoa convidada ainda</p>
                          <p className="text-xs theme-text-muted mt-1">Use o formulário acima para convidar pessoas</p>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {users.map((user) => (
                            <motion.div
                              key={user.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="flex items-center justify-between px-4 py-3 rounded-xl hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-all group border border-transparent hover:border-[color:color-mix(in_oklab,var(--card-border)_50%,transparent)]"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border-2 transition-all ${user.isOwner
                                  ? 'bg-gradient-to-br from-[color:var(--nt-primary)]/20 to-[color:var(--nt-primary)]/10 border-[color:var(--nt-primary)]/30 shadow-sm'
                                  : 'bg-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] border-[color:color-mix(in_oklab,var(--card-border)_70%,transparent)]'
                                  }`}>
                                  <User className={`w-5.5 h-5.5 ${user.isOwner ? 'text-[color:var(--nt-primary)]' : 'theme-text-muted'}`} weight={user.isOwner ? 'fill' : 'bold'} />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium theme-text truncate">
                                      {user.name}
                                    </span>
                                    {user.isOwner && (
                                      <span className="text-[10px] font-semibold text-[color:var(--nt-primary)] bg-[color:var(--nt-primary)]/10 px-2 py-0.5 rounded-md border border-[color:var(--nt-primary)]/20">
                                        VOCÊ
                                      </span>
                                    )}
                                  </div>
                                  {!user.isOwner && (
                                    <div className="text-xs theme-text-muted truncate mt-0.5 flex items-center gap-1.5">
                                      <Envelope className="w-3 h-3" weight="regular" />
                                      {user.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {user.isOwner ? (
                                <div className="text-xs font-semibold theme-text-muted px-3 py-1.5 rounded-lg bg-gradient-to-br from-[color:color-mix(in_oklab,var(--card-bg)_90%,transparent)] to-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] border theme-border flex items-center gap-1.5">
                                  <Lock className="w-3 h-3" weight="fill" />
                                  <span>Owner</span>
                                </div>
                              ) : (
                                <div className="relative">
                                  <button
                                    type="button"
                                    className="px-3 py-1.5 text-xs font-medium theme-text-muted hover:theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_80%,transparent)] active:scale-95 rounded-lg transition-all flex items-center gap-1.5 border border-transparent hover:border-[color:color-mix(in_oklab,var(--card-border)_50%,transparent)]"
                                  >
                                    <span className="opacity-70">{PERMISSION_ICONS[user.permission]}</span>
                                    <span>{PERMISSION_LABELS[user.permission]}</span>
                                    <CaretDown className="w-3 h-3" weight="bold" />
                                  </button>
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Link Sharing */}
                    <div className="pt-5 border-t theme-border">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[color:var(--nt-primary)]/20 to-[color:var(--nt-primary)]/10 flex items-center justify-center border border-[color:var(--nt-primary)]/20">
                          <LinkSimple className="w-4.5 h-4.5 text-[color:var(--nt-primary)]" weight="bold" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold theme-text">
                            Compartilhar via link
                          </div>
                          <p className="text-xs theme-text-muted mt-0.5">Gere um link para compartilhar rapidamente</p>
                        </div>
                      </div>
                      <div className="flex items-stretch gap-2">
                        <div className="relative flex-1" ref={linkPermissionDropdownRef}>
                          <button
                            type="button"
                            onClick={() => setIsLinkPermissionDropdownOpen(!isLinkPermissionDropdownOpen)}
                            className="w-full px-3.5 py-2.5 text-sm border theme-border rounded-lg bg-[color:var(--background)] theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] active:scale-95 transition-all flex items-center justify-between"
                          >
                            <span className="text-xs text-left flex items-center gap-2 flex-1 min-w-0">
                              <span className="opacity-70">{PERMISSION_ICONS[linkPermission]}</span>
                              <span className="truncate">
                                {linkPermission === 'can-view'
                                  ? 'Qualquer pessoa com o link pode visualizar'
                                  : linkPermission === 'can-edit'
                                    ? 'Qualquer pessoa com o link pode editar'
                                    : 'Apenas pessoas com acesso'}
                              </span>
                            </span>
                            <CaretDown className="w-3.5 h-3.5 flex-shrink-0 ml-2" weight="bold" />
                          </button>
                          <AnimatePresence>
                            {isLinkPermissionDropdownOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.15 }}
                                className="absolute left-0 right-0 top-full mt-1.5 rounded-lg border theme-border theme-bg shadow-xl overflow-hidden z-10 backdrop-blur-sm"
                              >
                                {(['can-view', 'can-edit'] as Permission[]).map((perm) => (
                                  <button
                                    key={perm}
                                    type="button"
                                    onClick={() => {
                                      setLinkPermission(perm);
                                      setIsLinkPermissionDropdownOpen(false);
                                    }}
                                    className={`w-full px-3.5 py-2.5 text-sm text-left transition-colors flex items-center gap-2.5 ${linkPermission === perm
                                      ? 'bg-[color:var(--nt-primary)]/10 theme-text font-medium border-l-2 border-[color:var(--nt-primary)]'
                                      : 'hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] theme-text'
                                      }`}
                                  >
                                    <span className="opacity-70">{PERMISSION_ICONS[perm]}</span>
                                    <span>
                                      {perm === 'can-view'
                                        ? 'Qualquer pessoa com o link pode visualizar'
                                        : 'Qualquer pessoa com o link pode editar'}
                                    </span>
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        <motion.button
                          type="button"
                          onClick={handleCopyLink}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="px-4 py-2.5 text-sm font-semibold border theme-border rounded-lg theme-text hover:bg-[color:color-mix(in_oklab,var(--card-bg)_85%,transparent)] transition-all flex items-center gap-2 flex-shrink-0 shadow-sm hover:border-[color:var(--nt-primary)]/30 hover:shadow-md"
                        >
                          {copied ? (
                            <>
                              <Check className="w-4 h-4 text-[color:var(--nt-primary)]" weight="bold" />
                              <span className="text-[color:var(--nt-primary)]">Copiado!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" weight="bold" />
                              <span>Copiar</span>
                            </>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Toggle SideChat button - only visible when chat is closed */}
          {!isSideChatOpen && onToggleSideChat && (
            <div className="relative group/tooltip">
              <button
                type="button"
                onClick={onToggleSideChat}
                className="group relative inline-flex items-center justify-center w-11 h-11 rounded-lg transition-all cursor-pointer"
                title="Abrir o DocsAgent"
                aria-label="Abrir o DocsAgent"
              >
                <div className="relative w-7 h-8 transition-transform duration-500 group-hover:rotate-[360deg]">
                  {/* Base logo - gray */}
                  <svg
                    className="absolute inset-0 w-full h-full"
                    fill="none"
                    viewBox="0 0 40 48"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      clipRule="evenodd"
                      d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z"
                      fill="currentColor"
                      fillRule="evenodd"
                      className="text-gray-400 dark:text-gray-500"
                    />
                  </svg>
                  {/* Hover overlay with gradient fill animation */}
                  <div
                    className="absolute inset-0 overflow-hidden transition-all duration-700 ease-out group-hover:[clip-path:inset(0_0_0_0)] [clip-path:inset(100%_0_0_0)]"
                  >
                    <svg
                      className="absolute inset-0 w-full h-full"
                      fill="none"
                      viewBox="0 0 40 48"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <defs>
                        <linearGradient id="agentGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                          <stop offset="0%" stopColor="#ec4899" />
                          <stop offset="35%" stopColor="#8b5cf6" />
                          <stop offset="70%" stopColor="#6927da" />
                          <stop offset="100%" stopColor="#4f46e5" />
                        </linearGradient>
                      </defs>
                      <path
                        clipRule="evenodd"
                        d="m20 41.1364c1.8693 0 3.6688-.2994 5.353-.8527-1.7175-.2891-3.5271-.8718-5.3517-1.7289-1.8251.8573-3.6351 1.4402-5.3529 1.7294 1.6838.553 3.4828.8522 5.3516.8522zm-16.28276-11.7805c.28897-1.7183.87187-3.529 1.72942-5.3547-.8578-1.8258-1.44092-3.6366-1.73007-5.3551-.55352 1.6844-.85295 3.4842-.85295 5.3539 0 1.8704.29966 3.6709.8536 5.3559zm3.60425-2.0098c-.16001.4683-.29664.9297-.40971 1.382-.88774 3.5509-.23652 5.9879 1.06875 7.2932 1.30527 1.3052 3.74227 1.9565 7.29317 1.0687.4525-.1131.914-.2498 1.3824-.4098-1.7678-1.1549-3.5052-2.5591-5.1409-4.1948-1.63517-1.6352-3.03901-3.372-4.19371-5.1393zm12.67971 7.8448c-2.1428-1.1725-4.3241-2.7862-6.3647-4.8268-2.0401-2.0401-3.6535-4.2207-4.82586-6.363 1.17244-2.1428 2.78616-4.324 4.82676-6.3646 2.0404-2.0404 4.2213-3.6539 6.3638-4.8263 2.1424 1.1723 4.3231 2.7858 6.3632 4.826 2.0407 2.0407 3.6545 4.222 4.827 6.3649-1.1724 2.1424-2.7859 4.3231-4.8261 6.3633-2.0404 2.0405-4.2215 3.6541-6.3641 4.8265zm12.6793-7.8447c-1.1548 1.7674-2.5586 3.5043-4.1939 5.1395-1.6354 1.6355-3.3726 3.0395-5.1402 4.1944.4682.1599.9295.2965 1.3817.4096 3.5509.8877 5.9879.2365 7.2932-1.0688 1.3052-1.3052 1.9565-3.7422 1.0687-7.2932-.113-.4521-.2496-.9133-.4095-1.3815zm1.8748-3.345c.8572-1.8245 1.44-3.634 1.7293-5.3514.5528 1.6834.8518 3.4819.8518 5.3502 0 1.869-.2993 3.6682-.8524 5.3521-.2891-1.7172-.8718-3.5265-1.7287-5.3509zm-9.2009-16.28444c-1.7179.28914-3.528.87204-5.3531 1.72941-1.8256-.85764-3.6361-1.44069-5.3544-1.72984 1.6842-.55336 3.4837-.85269 5.3531-.85269 1.8699 0 3.6698.29949 5.3544.85312zm8.7037 2.05754c-3.6125-3.57015-8.5779-5.7743-14.0581-5.7743-11.0457 0-20 8.9543-20 20s8.9543 20 20 20c5.488 0 10.4598-2.2104 14.0735-5.7896.0232-.0225.0462-.0453.0691-.0681.023-.023.0458-.0461.0684-.0693 3.5788-3.6137 5.789-8.5853 5.789-14.073 0-5.4788-2.203-10.443-5.7715-14.0552-.028-.02903-.0564-.05785-.085-.08647-.0283-.02828-.0568-.05629-.0854-.08403zm-17.4017 1.5465c-.4688-.1602-.9307-.2971-1.3835-.4103-3.551-.8877-5.98798-.2365-7.29325 1.0688s-1.9565 3.7423-1.06876 7.2932c.11322.4529.25005.9148.41032 1.3838 1.15492-1.7679 2.55915-3.5054 4.19489-5.1411 1.6354-1.6355 3.3727-3.0396 5.1403-4.1944zm16.0244 9.3353c-1.155-1.7679-2.5593-3.5055-4.1951-5.1413-1.6352-1.6352-3.3722-3.0391-5.1396-4.1939.4686-.1601.9303-.2969 1.3829-.41 3.5509-.8877 5.9879-.2365 7.2932 1.0687 1.3052 1.3053 1.9565 3.7423 1.0687 7.2933-.1132.4527-.2499.9145-.4101 1.3832z"
                        fill="url(#agentGradient)"
                        fillRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </button>
              {/* Tooltip */}
              <div className="absolute top-full right-0 mt-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium rounded-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none shadow-lg">
                Abrir o DocsAgent
                <div className="absolute bottom-full right-3 border-4 border-transparent border-b-gray-900 dark:border-b-gray-100" />
              </div>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}
